import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import { CatalogLanguagesPage } from '@/features/catalog';
import { chatbotSettingsRoutes } from '@/features/chatbot-settings';

import {
  AddressSettingsPage,
  BasketSettingsPage,
  CatalogSettingsPage,
  CheckoutSettingsPage,
  ContentSettingsPage,
  CustomerSettingsPage,
  DeliverySettingsPage,
  EngagementSettingsPage,
  ChannelPreviewPage,
  ErpPosifloraSettingsPage,
  MaxSettingsPage,
  InventorySettingsPage,
  MediaSettingsPage,
  NotificationSettingsPage,
  OrderSettingsPage,
  PaymentSettingsPage,
  PricingSettingsPage,
  RetailSettingsPage,
  SalesChannelsPage,
  SettingsIndexPage,
  TelegramSettingsPage,
  TenantSettingsPage,
} from './ui/pages';
import { RequireSection } from '@/shared/routing';

import { SettingsLayout } from './ui/SettingsLayout';

/**
 * Store-settings route subtree, mounted at `/settings` inside the app shell.
 *
 * Every screen renders inside {@link SettingsLayout} — a two-pane shell with a
 * grouped settings rail. The index renders a grouped, searchable overview; each
 * domain renders its own permission-gated screen. Unknown sub-paths fall back to
 * the overview.
 *
 * <p>Two tiers of gate, and both are needed. The `settings` section guard here refuses
 * the whole area to anyone who can view **no** settings domain — without it, such a user
 * reached a hub of locked cards and a rail of destinations that all refused them. Inside,
 * each domain screen still gates on its own `settings.<domain>.view` through
 * `SettingsScaffold`, because holding one domain must not open the other twenty-nine.
 */
export const settingsRoutes: RouteObject = {
  path: 'settings',
  element: (
    <RequireSection section="settings">
      <SettingsLayout />
    </RequireSection>
  ),
  children: [
    { index: true, element: <SettingsIndexPage /> },
    { path: 'delivery', element: <DeliverySettingsPage /> },
    { path: 'payments', element: <PaymentSettingsPage /> },
    { path: 'checkout', element: <CheckoutSettingsPage /> },
    { path: 'orders', element: <OrderSettingsPage /> },
    { path: 'pricing', element: <PricingSettingsPage /> },
    { path: 'catalog', element: <CatalogSettingsPage /> },
    { path: 'languages', element: <CatalogLanguagesPage /> },
    { path: 'customers', element: <CustomerSettingsPage /> },
    { path: 'notifications', element: <NotificationSettingsPage /> },
    { path: 'content', element: <ContentSettingsPage /> },
    { path: 'engagement', element: <EngagementSettingsPage /> },
    { path: 'channels', element: <SalesChannelsPage /> },
    { path: 'telegram', element: <TelegramSettingsPage /> },
    // plan-conversation-001 WP-MP.2: the Telegram-only texts editor is retired — its BFF paths
    // (`/api/v1/store/settings/telegram/messages/**`) now return `410 Gone`. Redirect kept for one
    // release (WP-MP.6 removes it), per adr-merchant-portal-019 D6.
    { path: 'telegram/messages', element: <Navigate to="/settings/chatbot/templates" replace /> },
    { path: 'max', element: <MaxSettingsPage /> },
    ...chatbotSettingsRoutes,
    { path: 'retail', element: <RetailSettingsPage /> },
    { path: 'web', element: <ChannelPreviewPage channel="web" /> },
    { path: 'ozon', element: <ChannelPreviewPage channel="ozon" /> },
    { path: 'wildberries', element: <ChannelPreviewPage channel="wildberries" /> },
    { path: 'yandex-market', element: <ChannelPreviewPage channel="yandex-market" /> },
    { path: 'lamoda', element: <ChannelPreviewPage channel="lamoda" /> },
    { path: 'tenant', element: <TenantSettingsPage /> },
    { path: 'store', element: <Navigate to="/store" replace /> },
    { path: 'address', element: <AddressSettingsPage /> },
    { path: 'inventory', element: <InventorySettingsPage /> },
    { path: 'media', element: <MediaSettingsPage /> },
    // plan-merchant-portal-019 WP2: `AiAgentSettingsPage` is retired — its content lives inside the
    // AI provider card on `/settings/chatbot` now (anchor `#ai`, design-chatbot-settings-page.md
    // §10). Redirect kept for bookmarks/deep links (adr-merchant-portal-019 D5).
    { path: 'ai-agents', element: <Navigate to="/settings/chatbot#ai" replace /> },
    { path: 'basket', element: <BasketSettingsPage /> },
    { path: 'erp', element: <ErpPosifloraSettingsPage /> },
    // The Automation tab (formerly the standalone Evotor-integration settings page) now lives on
    // the Retail page — redirect so existing links/bookmarks to the old route keep working.
    {
      path: 'integrations/evotor',
      element: <Navigate to="/settings/retail?tab=automation" replace />,
    },
    { path: '*', element: <Navigate to="/settings" replace /> },
  ],
};
